import deepFreeze from 'deep-freeze'
import {
  values,
  createWDE0025Error,
} from '@wix/wix-data-client-common-standalone'
import { type Schema } from '@wix/wix-data-schema-types'
import type { SchemasClient } from '@wix/wix-data-schemas-client'
import type { SchemasClientAdapter } from './types'

export const createSchemasClientAdapter = ({
  wixDataSchemasClient,
  fetchCollectionIdsFromDatasets,
  syncRoutersPromise,
}: {
  wixDataSchemasClient: SchemasClient
  fetchCollectionIdsFromDatasets: () => Promise<string[]>
  syncRoutersPromise?: Promise<void>
}): SchemasClientAdapter => {
  let schemasCache:
    | {
        status: 'ready'
        cache: Map<string, Schema>
      }
    | {
        status: 'initializing'
        promise: Promise<Map<string, Schema>>
      }
    | {
        status: 'clear'
      } = {
    status: 'clear',
  }

  const initializeCache = async (): Promise<Map<string, Schema>> => {
    if (syncRoutersPromise) {
      await syncRoutersPromise
    }

    const [existingSchemasById, collectionIdsFromDatasets] = await Promise.all([
      wixDataSchemasClient.list({}),
      fetchCollectionIdsFromDatasets(),
    ])
    const deletedCollectionIds = collectionIdsFromDatasets.filter(
      collectionId => !existingSchemasById[collectionId],
    )
    const deletedSchemasById = await wixDataSchemasClient.bulkGet(
      deletedCollectionIds,
      {
        includeDeletedCollections: true,
      },
    )
    const allSchemas = [
      ...values(deletedSchemasById),
      ...values(existingSchemasById),
    ]

    return new Map(
      allSchemas.map(schema => {
        return [schema.id, deepFreeze(schema)]
      }),
    )
  }

  const getCache = async (): Promise<Map<string, Schema>> => {
    if (schemasCache.status === 'clear') {
      const promise = initializeCache()
        .then(cache => {
          schemasCache = { status: 'ready', cache }
          ;(self as any).__wix_dbsm_editor_app_sled = {
            schemasLoaded: true,
          }
          return cache
        })
        .catch(e => {
          schemasCache = { status: 'clear' }
          throw new Error('Failed to initialize schemas cache', { cause: e })
        })
      schemasCache = {
        status: 'initializing',
        promise,
      }
      return promise
    }
    return schemasCache.status === 'initializing'
      ? schemasCache.promise
      : schemasCache.cache
  }

  const clearCache = async (): Promise<void> => {
    if (schemasCache.status === 'initializing') {
      try {
        await schemasCache.promise
      } catch {}
    }
    schemasCache = {
      status: 'clear',
    }
  }

  const updateCache = async (updatedSchemas: Schema[]) => {
    const cachedSchemasById = await getCache()
    updatedSchemas.forEach(schema => cachedSchemasById.set(schema.id, schema))
  }

  const listSchemas = async ({
    includeDeletedCollections,
  }: {
    includeDeletedCollections?: boolean
  } = {}): Promise<Schema[]> => {
    const schemas = Array.from((await getCache()).values())

    return includeDeletedCollections
      ? schemas
      : schemas.filter(schema => !schema.isDeleted)
  }

  const loadSchema = async (collectionId: string): Promise<Schema> => {
    const schema = (await getCache()).get(collectionId)
    if (schema) {
      return schema
    }

    const schemas = await wixDataSchemasClient.bulkGet([collectionId], {
      includeDeletedCollections: true,
    })
    if (schemas[collectionId]) {
      return schemas[collectionId]
    }

    throw createWDE0025Error(collectionId)
  }

  const saveSchema = async (
    collectionId: string,
    schema: Schema,
  ): Promise<void> => {
    const updatedSchemas = await wixDataSchemasClient.save(collectionId, schema)
    await updateCache(updatedSchemas)
  }

  const removeSchema = async (collectionId: string): Promise<void> => {
    const updatedSchemas = await wixDataSchemasClient.remove(collectionId)
    await updateCache(updatedSchemas)
  }

  const restoreSchema = async (collectionId: string): Promise<void> => {
    const restoredSchema = await wixDataSchemasClient.restore(collectionId)
    await updateCache([restoredSchema])
  }

  return {
    listSchemas,
    loadSchema,
    saveSchema,
    removeSchema,
    restoreSchema,
    clearCache,
  }
}
